import { effect, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, from, Observable, Subject } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import * as _ from 'lodash';
import { LocalstorageService } from '@gridscale/gs-services/localstorage.service';
import * as PartnerSelectors from './../partner/partner.selectors';
import { UserStoreApiService } from '../../@shared/services/userstoreapi.service';
import { Store } from '@ngrx/store';


interface KeySubscription<T = string | null> {
  realKey: string | string[];
  subject: BehaviorSubject<T>;
  lastValue: T;
  defaultValue: unknown;
}

interface UserStoreApiResonse {
  code: number;
  status: number;
  body: Record<string, any>
}

@Injectable({
  providedIn: 'root'
})
export class UserStoreService {
  readonly loaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  readonly #localStorageService = inject(LocalstorageService);
  readonly #userStoreApiService = inject(UserStoreApiService);

  readonly #data = signal<Record<string, any>>({});
  readonly #dataDirty = signal(false);
  readonly #store = inject(Store);
  readonly #subscribedKeys: { [key: string]: KeySubscription<any> } = {};


  constructor() {
    this.#init().then(() => {
      this.#syncUserStore();
    });

    effect(() => {
      const data = this.#data();

      _.forEach(this.#subscribedKeys, (subscription => {
        if (!_.isEqual(_.get(data, subscription.realKey), subscription.lastValue)) {
          subscription.subject.next(this.get(subscription.realKey, subscription.defaultValue));
          subscription.lastValue = this.get(subscription.realKey, subscription.defaultValue);
        }
      }));
    }, { allowSignalWrites: true })

  }

  async #init() {
    const partnerLoaded = await this.waitPartnerLoaded();
    if (partnerLoaded) {
      // const localUserData = this.#localStorageService.getItem('userStore', undefined);
      // if (localUserData) {
      //   // if we have local storage data, we save it to the endpoint once and delete the localStorage key
      //   try {
      //     await this.writeUserStore(JSON.parse(localUserData));
      //     this.#localStorageService.removeItem('userStore');
      //   } catch { /* we don't care */ }
      // }

      this.#data.set(await this.loadUserStore());

      this.loaded$.next(true);
    }
  }


  /**
   * regularely sync the userstore with the API
   */
  async #syncUserStore() {
    if (this.#dataDirty() && this.loaded$.value) {
      try {
        await this.writeUserStore(this.#data());
        this.#dataDirty.set(false);
      } catch {
        /* try it in next interval */
      }
    }

    window.setTimeout(() => this.#syncUserStore(), 1000);
  }

  getAll() {
    return _.cloneDeep(this.#data());
  }

  /**
   * get value from userStore
   */
  get<T = string | null>(key: string | string[], defaultValue?: T): T {
    return _.get(_.cloneDeep(this.#data()), key, defaultValue);
  }

  /**
   * Checks if userstore has value
   */
  has(key: string | string[]): boolean {
    return undefined !== _.get(this.#data(), key, undefined);
  }

  /**
   * Sets value in userstore
   */
  set(key: string | string[], value: unknown): void {
    if (!this.loaded$.value) {
      console.warn('Setting to userStore before it is loaded - value discarded!', key, value);
      return;
    }

    if ((typeof value === 'object' && value !== null) || Array.isArray(value)) {
      value = JSON.parse(JSON.stringify(value))
    }

    if (!_.isEqual(_.get(this.#data(), key), value)) {
      this.#data.set(
        _.set(
          {
            ...this.#data()
          },
          key,
          value
        )
      );

      this.#dataDirty.set(true);
    }
  }

  /**
   * subscribe to a value and get notified everytime it changes
   * @param key
   * @param defaultValue
   */
  subscribe<T = string | null>(key: string | string[], defaultValue?: T): Subject<T> {
    let keyStr: string;
    if (_.isArray(key)) {
      keyStr = key.join('/');
    } else {
      keyStr = key;
    }

    if (this.#subscribedKeys[keyStr] === undefined) {
      this.#subscribedKeys[keyStr] = {
        realKey: key,
        subject: new BehaviorSubject(this.get(key, defaultValue)),
        lastValue: this.get(key, defaultValue),
        defaultValue
      } as KeySubscription<T>;
    }

    return (this.#subscribedKeys[keyStr] as KeySubscription<T>).subject;
  }



  /**
   * clears the userstore. Shoud normally NOT BE USED - only in debug window
   * @deprecated
   */
  clear(): Observable<boolean | void> {
    this.#data.set({});
    return from(this.writeUserStore({}));
  }


  get isInitialized$(): Observable<boolean> {
    return this.loaded$.asObservable();
  }



  /**
   * returns once the partner is loaded
   */
  async waitPartnerLoaded() {
    return firstValueFrom(
      this.#store.select(PartnerSelectors.isPartnerLoaded).pipe(
        filter(isLoaded => !!isLoaded),
      )
    );
  }

  /**
   * load user store data from the API
   */
  async loadUserStore(): Promise<Record<string, any>> {
    return firstValueFrom(
      this.#userStoreApiService.get<UserStoreApiResonse>('/api/v2/store/user').pipe(
        map(result => result.body)
      )
    );
  }

  /**
   * writes user store data to the API
   */
  async writeUserStore(data: Record<string, any>): Promise<void> {
    return firstValueFrom(
      this.#userStoreApiService.post('/api/v2/store/user', data)
    );
  }

}
